'use client';

import { useEffect, useState } from 'react';
import {
  Eye, Save, Send, CheckCircle2, AlertTriangle, Check, ArrowLeft, ChevronLeft,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  crearSolicitud, actualizarSolicitud, obtenerSolicitud, listarFirmantes, enviarActa,
  CANALES, DECISIONES, ESTADO_ACTA_LABEL, comiteCierra, comiteContinua, labelDecision,
  type Solicitud, type Firmante, type Canal, type DecisionComite,
  type RutaSolicitud, type ModalidadFisico, type CategoriaAdjunto,
  type TipoIngresoQueja,
} from '@/lib/preRadicacion';
import { DEPARTAMENTOS, municipiosDe } from '@/lib/colombia';
import { labelTipoIngreso } from '@/lib/expedientes';
import ActaRepartoDialog from '@/components/ActaRepartoDialog';
import AvisoErrorCentral from '@/components/AvisoErrorCentral';
import AvisoExitoCentral from '@/components/AvisoExitoCentral';
import CampoAdjuntos from '@/components/CampoAdjuntos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const soloFecha = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

// «¿Quién interpone…?»: mismos catálogos que la Etapa 2 (el backend valida a fondo).
const TIPOS_INGRESO: TipoIngresoQueja[] = ['DE_OFICIO', 'QUEJOSO', 'INFORME', 'ANONIMO'];
const TIPOS_DOC = ['CC', 'CE', 'TI', 'NIT', 'PASAPORTE'];

// Pasos del asistente (rediseño ago-2026): SOLO 2. Elegir la ruta al final del
// Ingreso lleva directo al paso 2, donde el propio bloque de la ruta (Reparto o
// Comité-PROCEDE) trae las personas del flujo + previsualizar/enviar el acta.
// El antiguo paso «Acta de reparto» desapareció (era redundante).
const PASOS = ['Ingreso', 'Ruta y decisión'];

// A qué paso pertenece cada faltante que reporta el backend (para que el chip
// del banner salte directo al paso donde se corrige).
const PASO_POR_FALTANTE: Record<string, number> = {
  'Fecha de ingreso': 0,
  'Canal de ingreso': 0,
  'Radicado de origen': 0,
  'Radicado de la ventanilla única': 0, // canal CORREO (no tiene casilla «Radicado»)
  'Ruta: Reparto o Comité de queja': 0, // se elige al FINAL del paso de Ingreso
  'Fecha del comité de queja': 1,
  'Decisión del comité': 1,
  'El comité decidió Inhibitorio: el proceso termina': 1,
  'El comité decidió Remisión por competencia: el proceso termina': 1,
  'El comité decidió Otro: el proceso termina': 1,
  'El comité decidió No procede: el proceso termina': 1, // legado
  'El comité decidió NO PROCEDE: el proceso termina': 1, // legado
  // Legado (solicitudes viejas sin ruta):
  'Decisión de comité': 1,
  'El comité decidió NO PROCEDE: no se radica': 1,
  'Abogado asignado': 1,
  'Acta de reparto firmada por el Director': 1,
  'Asignación del proceso (Expedientes aprobados)': 1,
};

interface FormSol {
  fechaIngreso: string; canal: Canal | '';
  radicadoOrigen: string;
  // Canal físico
  modalidadFisico: ModalidadFisico | '';
  transcripcionTexto: string;
  observaciones: string; // en FÍSICO reemplazan al radicado
  // Aportes (Sí/No)
  aportoDocumentos: boolean; aportoMedioMagnetico: boolean;
  // Ventanilla única (sección fija; hoy solo en el canal Correo)
  vuRadicado: string; fechaRespuestaVentanilla: string;
  // ¿Quién interpone la notificación disciplinaria? (siempre visible)
  tipoIngresoQueja: TipoIngresoQueja | '';
  qNombre: string; qTipoDoc: string; qNumDoc: string; qDireccion: string;
  qDepartamento: string; qMunicipio: string; qTelefono: string; qCorreo: string;
  // Ruta y decisión
  ruta: RutaSolicitud | '';
  justificacionReparto: string;
  fechaComite: string; comiteReprogramado: boolean; fechaComiteNueva: string;
  decisionComite: DecisionComite | '';
  // REMISIÓN POR COMPETENCIA → a quién se remite · OTRO → «indique cuál»
  comiteRemisionDestino: string; comiteOtroDetalle: string;
  justificacionComite: string; fechaDecisionComite: string;
  // Asignación / acta. El director de instrucción se agrega en las «personas del
  // flujo» del reparto (obligatorio junto con el abogado para enviar el acta).
  abogadoId: string; tecnologoApoyoId: string; sustanciadorId: string; directorId: string;
}

function aForm(s: Solicitud | null): FormSol {
  return {
    fechaIngreso: soloFecha(s?.fecha_ingreso), canal: (s?.canal ?? '') as Canal | '',
    radicadoOrigen: s?.radicado_origen ?? '',
    modalidadFisico: (s?.modalidad_fisico ?? '') as ModalidadFisico | '',
    transcripcionTexto: s?.transcripcion_texto ?? '',
    observaciones: s?.observaciones ?? '',
    aportoDocumentos: s?.aporto_documentos ?? false,
    aportoMedioMagnetico: s?.aporto_medio_magnetico ?? false,
    vuRadicado: s?.vu_radicado ?? '',
    fechaRespuestaVentanilla: soloFecha(s?.fecha_respuesta_ventanilla),
    tipoIngresoQueja: (s?.tipo_ingreso_queja ?? '') as TipoIngresoQueja | '',
    qNombre: s?.quejoso_nombre ?? '',
    qTipoDoc: s?.quejoso_tipo_documento ?? '',
    qNumDoc: s?.quejoso_numero_documento ?? '',
    qDireccion: s?.quejoso_direccion ?? '',
    qDepartamento: s?.quejoso_departamento ?? '',
    qMunicipio: s?.quejoso_municipio ?? '',
    qTelefono: s?.quejoso_telefono ?? '',
    qCorreo: s?.quejoso_correo ?? '',
    ruta: (s?.ruta ?? '') as RutaSolicitud | '',
    justificacionReparto: s?.justificacion_reparto ?? '',
    fechaComite: soloFecha(s?.fecha_comite),
    comiteReprogramado: s?.comite_reprogramado ?? false,
    fechaComiteNueva: soloFecha(s?.fecha_comite_nueva),
    decisionComite: (s?.decision_comite ?? '') as DecisionComite | '',
    comiteRemisionDestino: s?.comite_remision_destino ?? '',
    comiteOtroDetalle: s?.comite_otro_detalle ?? '',
    justificacionComite: s?.justificacion_comite ?? '',
    fechaDecisionComite: soloFecha(s?.fecha_decision_comite),
    abogadoId: s?.abogado_id ?? '', tecnologoApoyoId: s?.tecnologo_apoyo_id ?? '',
    sustanciadorId: s?.sustanciador_id ?? '', directorId: s?.director_id ?? '',
  };
}

/**
 * Filas de «Personas del flujo»: SIEMPRE dos casillas fijas por rol —
 * la 1 es el abogado especialista y la 2 el director de instrucción.
 */
function flujoInicial(s: Solicitud | null): string[] {
  return [s?.sustanciador_id ?? '', s?.director_id ?? ''];
}

/**
 * Etapa 1 del Libro radicador (rediseño ago-2026): ingreso por canal con adjuntos
 * MÚLTIPLES por categoría (SAIA · Correo · Físico), ruta REPARTO/COMITÉ y
 * cadena de aprobación del acta de reparto (técnico → abogado especialista →
 * director firma). La asignación final (fechas/plazo/trazabilidad) se hace en
 * «Casos aprobados» cuando el director firma.
 */
export default function PreRadicacionForm({
  solicitud,
  onGuardada,
}: {
  solicitud: Solicitud | null;
  onGuardada: (s: Solicitud) => void;
}) {
  const [form, setForm] = useState<FormSol>(() => aForm(solicitud));
  const [firmantes, setFirmantes] = useState<Firmante[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [enviandoActa, setEnviandoActa] = useState(false);
  const [actaAbierta, setActaAbierta] = useState(false); // diálogo de previsualización del acta
  const [paso, setPaso] = useState(0); // 0=ingreso · 1=ruta · 2=acta
  // Filas de «Personas del flujo» del reparto (ids de firmantes; '' = casilla vacía).
  const [flujo, setFlujo] = useState<string[]>(() => flujoInicial(solicitud));
  // Mensajes CENTRADOS en pantalla (mismo diseño en error y éxito; sin toasts).
  const [errorCentral, setErrorCentral] = useState<string | null>(null);
  const [exitoCentral, setExitoCentral] = useState<string | null>(null);

  // Sincroniza el form cuando cambia la solicitud cargada (p. ej. al retomar una).
  useEffect(() => {
    setForm(aForm(solicitud));
    setFlujo(flujoInicial(solicitud));
  }, [solicitud?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { listarFirmantes().then(setFirmantes).catch(() => setFirmantes([])); }, []);

  function set<K extends keyof FormSol>(k: K, v: FormSol[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // La sección de ventanilla única aplica SOLO a Correo: FÍSICO se retiró y el
  // canal OTRO se eliminó (ambos pedidos del cliente, ago-06).
  const esVentanilla = form.canal === 'CORREO';
  // CORREO no tiene casilla «Radicado» (pedido del cliente ago-06): el radicado de
  // la ventanilla única es el que se usa como radicado del proceso.
  const esCorreo = form.canal === 'CORREO';
  // FÍSICO NO maneja radicado (ni el de origen ni el de ventanilla única): en su
  // lugar se capturan OBSERVACIONES. El expediente tomará su radicado del sistema.
  const esFisico = form.canal === 'FISICO';
  // ¿Se muestran los bloques de aportes? (físico solo en la modalidad FÍSICA).
  const conAportes = form.canal === 'SAIA' || form.canal === 'CORREO'
    || (form.canal === 'FISICO' && form.modalidadFisico === 'FISICA');
  // Notificación anónima: no se piden datos personales, solo la procedencia.
  const anonimo = form.tipoIngresoQueja === 'ANONIMO';
  // Ambas casillas del flujo son LISTAS: la 1 ofrece los ABOGADOS ESPECIALISTAS
  // y la 2 los directores de instrucción, para que quien diligencia elija a cuál
  // incluir. ⚠️ CAMBIADO (ago-06, pedido del cliente): la casilla 1 ya NO lista a
  // TODOS los abogados sustanciadores (contratista/funcionario) — solo al
  // especialista, que es quien revisa y firma el acta de reparto.
  const directores = firmantes.filter((f) => f.rol === 'DIRECTOR_INSTRUCCION');
  const especialistas = firmantes.filter((f) => f.rol === 'ABOGADO_ESPECIALISTA');

  /**
   * Actualiza las casillas del flujo: la 1 (índice 0) es el abogado especialista
   * (revisa el acta) y la 2 (índice 1) el director de instrucción (la firma).
   */
  function actualizarFlujo(next: string[]) {
    setFlujo(next);
    setForm((f) => ({ ...f, sustanciadorId: next[0] ?? '', directorId: next[1] ?? '' }));
  }

  const adjuntosDe = (c: CategoriaAdjunto) =>
    (solicitud?.adjuntos ?? []).filter((a) => a.categoria === c);

  // ¿Cada paso quedó diligenciado? (para el chulo del stepper). El Ingreso
  // incluye la RUTA (Reparto/Comité); el paso 2 exige las personas del flujo
  // (salvo Comité-NO PROCEDE, donde el proceso termina).
  const pasoDone = [
    Boolean(form.fechaIngreso && form.canal && form.ruta),
    form.ruta === 'REPARTO' ? Boolean(form.sustanciadorId && form.directorId)
      : form.ruta === 'COMITE'
        ? comiteCierra(form.decisionComite)
          || (comiteContinua(form.decisionComite) && Boolean(form.sustanciadorId && form.directorId))
        : false,
  ];

  // Mini-resumen de cada paso, visible bajo su rótulo en el stepper.
  const resumenPaso = [
    form.canal
      ? `${CANALES.find((c) => c.v === form.canal)?.label ?? form.canal}${form.fechaIngreso ? ` · ${form.fechaIngreso}` : ''}${form.ruta ? ` · ${form.ruta === 'REPARTO' ? 'Reparto' : 'Comité'}` : ''}`
      : 'Sin diligenciar',
    form.ruta === 'REPARTO'
      ? `Reparto${solicitud?.acta_estado ? ` · ${ESTADO_ACTA_LABEL[solicitud.acta_estado]}` : form.sustanciadorId ? ` · ${firmantes.find((f) => f.id === form.sustanciadorId)?.nombre ?? ''}` : ''}`
      : form.ruta === 'COMITE'
        ? `Comité${form.decisionComite ? ` · ${DECISIONES.find((d) => d.v === form.decisionComite)?.label ?? ''}` : ''}`
        : 'Sin diligenciar',
  ];

  /** Recarga el DETALLE (incluye adjuntos/trazabilidad) y lo sube al padre. */
  async function recargar(id: string) {
    try { onGuardada(await obtenerSolicitud(id)); } catch { /* el guardado ya avisó */ }
  }

  /**
   * Crea la solicitud mínima si aún no existe (la usan los campos de adjuntos:
   * los archivos necesitan un id para colgarse).
   */
  async function asegurarSolicitud(): Promise<string | null> {
    if (solicitud) return solicitud.id;
    if (!form.fechaIngreso || !form.canal) {
      setErrorCentral('Indique primero la fecha de ingreso y el canal.');
      return null;
    }
    const nueva = await crearSolicitud({ fechaIngreso: form.fechaIngreso, canal: form.canal as Canal });
    onGuardada(nueva);
    return nueva.id;
  }

  function cuerpoGuardar() {
    return {
      fechaIngreso: form.fechaIngreso,
      canal: form.canal as Canal,
      // CORREO: el backend lo deriva del radicado de ventanilla única.
      // FÍSICO: ese canal no maneja radicado, no se envía nada.
      radicadoOrigen:
        esCorreo || esFisico ? undefined : (form.radicadoOrigen.trim() || undefined),
      // Observaciones del ingreso (hoy solo se capturan en FÍSICO).
      observaciones: esFisico ? form.observaciones.trim() : undefined,
      // Ventanilla única ya no es checkbox: aplica SIEMPRE en correo/físico/otro.
      porVentanillaUnica: esVentanilla,
      fechaRespuestaVentanilla: esVentanilla ? (form.fechaRespuestaVentanilla || undefined) : undefined,
      vuRadicado: esVentanilla ? (form.vuRadicado.trim() || undefined) : undefined,
      modalidadFisico: form.canal === 'FISICO' ? (form.modalidadFisico || undefined) : undefined,
      transcripcionTexto:
        form.canal === 'FISICO' && form.modalidadFisico === 'VERBAL'
          ? (form.transcripcionTexto || undefined)
          : undefined,
      aportoDocumentos: conAportes ? form.aportoDocumentos : false,
      aportoMedioMagnetico: conAportes ? form.aportoMedioMagnetico : false,
      // ¿Quién interpone? En ANÓNIMO solo viaja la procedencia; en el resto, los
      // datos de la persona. Se envían siempre (cadena vacía = limpiar el dato).
      tipoIngresoQueja: form.tipoIngresoQueja || undefined,
      quejosoNombre: anonimo ? '' : form.qNombre.trim(),
      quejosoTipoDocumento: anonimo || !form.qTipoDoc ? undefined : form.qTipoDoc,
      quejosoNumeroDocumento: anonimo ? '' : form.qNumDoc.trim(),
      quejosoDireccion: anonimo ? '' : form.qDireccion.trim(),
      quejosoDepartamento: anonimo ? '' : form.qDepartamento,
      quejosoMunicipio: anonimo ? '' : form.qMunicipio,
      quejosoTelefono: anonimo ? '' : form.qTelefono.trim(),
      quejosoCorreo: anonimo ? '' : form.qCorreo.trim(),
      ruta: form.ruta || undefined,
      justificacionReparto: form.ruta === 'REPARTO' ? (form.justificacionReparto.trim() || undefined) : undefined,
      fechaComite: form.ruta === 'COMITE' ? (form.fechaComite || undefined) : undefined,
      comiteReprogramado: form.ruta === 'COMITE' ? form.comiteReprogramado : false,
      fechaComiteNueva:
        form.ruta === 'COMITE' && form.comiteReprogramado ? (form.fechaComiteNueva || undefined) : undefined,
      decisionComite: form.ruta === 'COMITE' ? (form.decisionComite || undefined) : undefined,
      justificacionComite: form.justificacionComite.trim() || undefined,
      // Solo viajan con su decisión (si se cambia de opción, se limpian).
      comiteRemisionDestino:
        form.decisionComite === 'REMISION_COMPETENCIA' ? form.comiteRemisionDestino.trim() : '',
      comiteOtroDetalle:
        form.decisionComite === 'OTRO' ? form.comiteOtroDetalle.trim() : '',
      fechaDecisionComite: form.fechaDecisionComite || undefined,
      abogadoId: form.abogadoId || undefined,
      tecnologoApoyoId: form.tecnologoApoyoId || undefined,
      sustanciadorId: form.sustanciadorId || undefined,
      directorId: form.directorId || undefined,
    };
  }

  /**
   * Guarda la solicitud. Con `silencioso` NO muestra el aviso de éxito (lo usa
   * «Enviar al abogado especialista» para que salga UN solo aviso, no dos).
   */
  async function guardar(silencioso = false): Promise<Solicitud | null> {
    if (!form.fechaIngreso || !form.canal) {
      setErrorCentral('Indique la fecha de ingreso y el canal.');
      return null;
    }
    setGuardando(true);
    try {
      // `crear` solo acepta datos de ingreso; el resto va por `actualizar`.
      const base = solicitud
        ?? await crearSolicitud({ fechaIngreso: form.fechaIngreso, canal: form.canal as Canal });
      await actualizarSolicitud(base.id, cuerpoGuardar());
      const full = await obtenerSolicitud(base.id);
      onGuardada(full);
      if (!silencioso) {
        if (full.completa) {
          setExitoCentral('Pre-radicación completa. Continúe con los datos de la notificación disciplinaria.');
        } else if (full.decision_comite === 'NO_PROCEDE' && full.ruta === 'COMITE') {
          setExitoCentral('Guardado. El comité decidió NO PROCEDE: el proceso termina aquí.');
        } else {
          setExitoCentral(`Guardado en «Expedientes no iniciados». Falta: ${full.faltantes.join(' · ')}.`);
        }
      }
      return full;
    } catch (err) {
      setErrorCentral(
        err instanceof Error && err.message
          ? `No se pudo guardar: ${err.message}`
          : 'No se pudo guardar la solicitud.',
      );
      return null;
    } finally {
      setGuardando(false);
    }
  }

  /**
   * Guarda y ENVÍA el acta a revisión del abogado especialista elegido en las
   * personas del flujo (obligatorio junto con el director de instrucción).
   */
  async function guardarYEnviarActa() {
    if (!form.sustanciadorId) {
      setErrorCentral('Agregue el abogado especialista al flujo antes de enviar el acta.');
      return;
    }
    if (!form.directorId) {
      setErrorCentral('Agregue el director de instrucción al flujo: es obligatorio para enviar.');
      return;
    }
    setEnviandoActa(true);
    try {
      // Guardado silencioso: el aviso de éxito es UNO solo (el del envío).
      const full = await guardar(true);
      if (!full) return;
      await enviarActa(full.id, form.sustanciadorId);
      await recargar(full.id);
      setExitoCentral('El acta fue enviada al abogado especialista para su aprobación y firma.');
    } catch (err) {
      setErrorCentral(
        err instanceof Error && err.message
          ? `No se pudo enviar el acta: ${err.message}`
          : 'No se pudo enviar el acta.',
      );
    } finally {
      setEnviandoActa(false);
    }
  }

  // Previsualiza el acta dentro de la app (diálogo con el .docx oficial vía docx-preview).
  function previsualizarActa() {
    if (!solicitud) { setErrorCentral('Guarde primero la solicitud.'); return; }
    setActaAbierta(true);
  }

  // La cadena del acta admite enviar solo en borrador/devuelta (o primera vez).
  const actaEditable = !solicitud?.acta_estado
    || solicitud.acta_estado === 'BORRADOR'
    || solicitud.acta_estado === 'DEVUELTA_TECNICO';

  /**
   * Bloque COMPARTIDO del acta (rutas Reparto y Comité-PROCEDE): estado de la
   * cadena, personas del flujo (abogado + director obligatorios) y los botones
   * de previsualizar / enviar al abogado especialista.
   */
  function bloqueActa() {
    return (
      <>
        {/* Estado actual de la cadena del acta. */}
        {solicitud?.acta_estado && (
          <div className={cn(
            'flex items-start gap-2.5 rounded-lg border p-3 text-sm',
            solicitud.acta_estado === 'FIRMADA'
              ? 'border-primary/30 bg-primary/5'
              : solicitud.acta_estado === 'DEVUELTA_TECNICO'
                ? 'border-destructive/30 bg-destructive/5'
                : 'border-amber-300/60 bg-amber-50 dark:border-amber-400/30 dark:bg-amber-950/30',
          )}>
            {solicitud.acta_estado === 'FIRMADA'
              ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
              : <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />}
            <div>
              <p className="font-medium">{ESTADO_ACTA_LABEL[solicitud.acta_estado]}</p>
              {solicitud.acta_estado === 'DEVUELTA_TECNICO' && solicitud.acta_observacion && (
                <p className="text-destructive/90">Observación: {solicitud.acta_observacion}</p>
              )}
              {solicitud.acta_estado === 'FIRMADA' && (
                <p className="text-muted-foreground">
                  El director firmó el acta: asigne el proceso en <strong>«Expedientes aprobados»</strong>.
                </p>
              )}
            </div>
          </div>
        )}

        {/* PERSONAS DEL FLUJO: dos casillas FIJAS por rol — 1 = abogado
            especialista (solo lista ABOGADO_ESPECIALISTA) · 2 = director de
            instrucción (solo lista directores). */}
        <div className="space-y-3 rounded-md border p-3">
          <Label>Personas del flujo</Label>
          <p className="text-xs text-muted-foreground">
            Agregue el <strong>abogado especialista</strong> que revisará el acta y el{' '}
            <strong>director de instrucción</strong> que la aprobará y firmará. Ambos son
            obligatorios para poder enviarla. El abogado que recibirá el expediente se asigna
            después, en «Expedientes aprobados».
          </p>
          {/* Sin especialistas activos no hay a quién enviarle el acta: se avisa en
              vez de dejar un desplegable vacío sin explicación. */}
          {especialistas.length === 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-200">
              No hay ningún <strong>abogado especialista</strong> activo registrado. Pida que creen
              uno en Usuarios para poder enviar el acta.
            </p>
          )}
          <ol className="space-y-2">
            {flujo.map((id, i) => {
              const esDirector = i === 1;
              const opciones = esDirector ? directores : especialistas;
              const rotulo = esDirector ? 'Director de instrucción' : 'Abogado especialista';
              return (
                <li key={i} className="flex flex-wrap items-center gap-2">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {i + 1}
                  </span>
                  <div className="min-w-40 flex-1">
                    <Select
                      value={id}
                      onValueChange={(v) => actualizarFlujo(flujo.map((x, j) => (j === i ? v : x)))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={`Seleccione el ${rotulo.toLowerCase()}…`} />
                      </SelectTrigger>
                      <SelectContent>
                        {opciones.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className={cn(
                    'shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium',
                    esDirector
                      ? 'border-primary/40 bg-primary/5 text-primary'
                      : 'border-border bg-muted text-muted-foreground',
                  )}>
                    {rotulo}
                  </span>
                  <Button
                    type="button" size="icon" variant="ghost" className="size-7 shrink-0 text-destructive"
                    aria-label="Quitar" disabled={!id}
                    onClick={() => actualizarFlujo(flujo.map((x, j) => (j === i ? '' : x)))}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              );
            })}
          </ol>
          {/* Chulos de lo obligatorio para enviar. */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium',
              form.sustanciadorId
                ? 'border-primary/40 bg-primary/5 text-primary'
                : 'border-amber-400/60 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
            )}>
              {form.sustanciadorId ? <Check className="size-3" /> : <AlertTriangle className="size-3" />}
              Abogado especialista
            </span>
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium',
              form.directorId
                ? 'border-primary/40 bg-primary/5 text-primary'
                : 'border-amber-400/60 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
            )}>
              {form.directorId ? <Check className="size-3" /> : <AlertTriangle className="size-3" />}
              Director de instrucción
            </span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          El abogado especialista da el <strong>visto bueno</strong> del acta; la firman el
          Director y quien la elabora.
        </p>

        {/* Previsualizar + enviar al sustanciador elegido en el flujo. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={previsualizarActa}>
            <Eye className="size-4" /> Previsualizar acta de reparto
          </Button>
          <Button
            type="button"
            onClick={guardarYEnviarActa}
            disabled={!form.sustanciadorId || !form.directorId || !actaEditable || enviandoActa || guardando}
          >
            <Send className="size-4" />
            {enviandoActa ? 'Enviando…' : 'Enviar al abogado especialista'}
          </Button>
        </div>
        {!actaEditable && solicitud?.acta_estado !== 'FIRMADA' && (
          <p className="text-xs text-muted-foreground">
            El acta está en revisión: no se puede reenviar hasta que la aprueben o la devuelvan.
          </p>
        )}
      </>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stepper conectado: círculos unidos por línea, rótulo y mini-resumen. */}
      <ol className="flex items-start">
        {PASOS.map((label, i) => {
          const activo = paso === i;
          const done = pasoDone[i];
          return (
            <li key={label} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <span className={cn('h-0.5 flex-1', i === 0 ? 'opacity-0' : done || pasoDone[i - 1] ? 'bg-primary/40' : 'bg-border')} />
                <button
                  type="button"
                  onClick={() => setPaso(i)}
                  aria-current={activo ? 'step' : undefined}
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                    done
                      ? 'border-primary bg-primary text-primary-foreground'
                      : activo
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/50',
                    activo && 'ring-2 ring-primary/25 ring-offset-2 ring-offset-background',
                  )}
                >
                  {done ? <Check className="size-4" /> : i + 1}
                </button>
                <span className={cn('h-0.5 flex-1', i === PASOS.length - 1 ? 'opacity-0' : done ? 'bg-primary/40' : 'bg-border')} />
              </div>
              <button
                type="button"
                onClick={() => setPaso(i)}
                className="mt-1.5 flex min-w-0 max-w-full flex-col items-center rounded-md px-2 py-1 text-center transition-colors hover:bg-primary/5"
              >
                <span className={cn('text-sm leading-tight', activo ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                  {label}
                </span>
                <span className={cn(
                  'max-w-full truncate text-xs',
                  pasoDone[i] ? 'text-primary' : 'text-muted-foreground/70',
                )}>
                  {resumenPaso[i]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Paso 1 · Ingreso por canal */}
      {paso === 0 && (
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="fi">Fecha de ingreso</Label>
            <Input id="fi" type="date" value={form.fechaIngreso} onChange={(e) => set('fechaIngreso', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>¿Por dónde ingresó?</Label>
            <Select value={form.canal} onValueChange={(v) => set('canal', v as Canal)}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
              <SelectContent>
                {CANALES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ¿QUIÉN INTERPONE LA NOTIFICACIÓN DISCIPLINARIA? (ago-06, pedido del
            cliente): va justo DESPUÉS de «Fecha de ingreso / ¿Por dónde ingresó?»
            y antes de lo propio del canal. SIEMPRE se muestra, sea cual sea el
            canal (no depende de él). Lo que se llene aquí precarga la Etapa 2,
            donde todavía se puede completar o corregir (p. ej. una anónima que
            después se identifica). */}
        <div className="space-y-4 rounded-md border p-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">
            ¿Quién interpone la notificación disciplinaria?
          </p>
          <div className="space-y-2">
            <Label>De oficio · Quejoso · Informe · Anónimo</Label>
            <Select
              value={form.tipoIngresoQueja || undefined}
              onValueChange={(v) => set('tipoIngresoQueja', v as TipoIngresoQueja)}
            >
              <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
              <SelectContent>
                {TIPOS_INGRESO.map((t) => (
                  <SelectItem key={t} value={t}>{labelTipoIngreso(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* De oficio / Quejoso / Informe: datos de la persona. */}
          {form.tipoIngresoQueja && !anonimo && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="qnom">Nombre</Label>
                <Input id="qnom" value={form.qNombre} onChange={(e) => set('qNombre', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de documento</Label>
                <Select value={form.qTipoDoc || undefined} onValueChange={(v) => set('qTipoDoc', v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOC.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="qdoc">Número de documento</Label>
                <Input id="qdoc" inputMode="numeric" value={form.qNumDoc} onChange={(e) => set('qNumDoc', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qtel">Teléfono</Label>
                <Input id="qtel" value={form.qTelefono} onChange={(e) => set('qTelefono', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qcor">Correo</Label>
                <Input id="qcor" type="email" value={form.qCorreo} onChange={(e) => set('qCorreo', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qdir">Dirección</Label>
                <Input id="qdir" value={form.qDireccion} onChange={(e) => set('qDireccion', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Departamento</Label>
                <Select
                  value={form.qDepartamento || undefined}
                  onValueChange={(v) => { set('qDepartamento', v); set('qMunicipio', ''); }}
                >
                  <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                  <SelectContent position="popper">
                    {DEPARTAMENTOS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Municipio</Label>
                <Select value={form.qMunicipio || undefined} onValueChange={(v) => set('qMunicipio', v)} disabled={!form.qDepartamento}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={form.qDepartamento ? 'Seleccione…' : 'Elija primero el departamento'} />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {municipiosDe(form.qDepartamento).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Anónimo: no se registran datos personales (el cliente pidió quitar
              también el campo «¿de dónde proviene?» de esta etapa). */}
          {anonimo && (
            <p className="text-sm text-muted-foreground">
              Notificación disciplinaria anónima: no se registran datos personales.
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            Opcional en esta etapa. Lo que registre aquí se carga en la Etapa 2, donde
            podrá completarlo o corregirlo si después consiguen los datos.
          </p>
        </div>

        {/* Sin casilla «Radicado» en CORREO (manda el de VENTANILLA ÚNICA, más
            abajo en su sección) ni en FÍSICO (ese canal no maneja radicado: en su
            lugar se capturan observaciones). Pedido del cliente ago-06. */}
        {form.canal && !esCorreo && !esFisico && (
          <div className="space-y-2">
            <Label htmlFor="rad">
              {form.canal === 'SAIA' ? 'Radicado SAIA' : 'Radicado'}{' '}
              <span className="text-xs text-muted-foreground">(se le agrega -XXX consecutivo)</span>
            </Label>
            <Input id="rad" value={form.radicadoOrigen} onChange={(e) => set('radicadoOrigen', e.target.value)} placeholder="Ej.: 2026-000123" />
            {solicitud?.radicado_completo && (
              <p className="text-xs text-muted-foreground">
                Radicado asignado:{' '}
                <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 font-medium text-primary">
                  {solicitud.radicado_completo}
                </span>
              </p>
            )}
          </div>
        )}

        {/* FÍSICO: en lugar del radicado se capturan OBSERVACIONES del ingreso. */}
        {esFisico && (
          <div className="space-y-2">
            <Label htmlFor="obs">Observaciones</Label>
            <Textarea id="obs" rows={3} value={form.observaciones}
              onChange={(e) => set('observaciones', e.target.value)}
              placeholder="Anote lo relevante del ingreso físico…" />
            <p className="text-xs text-muted-foreground">
              El ingreso físico no maneja radicado: el expediente tomará el radicado
              del sistema al radicarse la Etapa 2.
            </p>
          </div>
        )}

        {/* PDF principal según el canal (múltiples con «+»). */}
        {form.canal === 'SAIA' && (
          <CampoAdjuntos label="PDF SAIA" categoria="SAIA" solicitudId={solicitud?.id}
            adjuntos={adjuntosDe('SAIA')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
        )}
        {form.canal === 'CORREO' && (
          <CampoAdjuntos label="PDF del correo que llegó a la dirección" categoria="CORREO" solicitudId={solicitud?.id}
            adjuntos={adjuntosDe('CORREO')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
        )}

        {/* Canal FÍSICO: modalidad verbal (transcripción) o física. */}
        {form.canal === 'FISICO' && (
          <div className="space-y-3 rounded-md border p-3">
            <Label>Opciones</Label>
            <div className="flex gap-2">
              <Button type="button" variant={form.modalidadFisico === 'VERBAL' ? 'default' : 'outline'} className="flex-1"
                onClick={() => set('modalidadFisico', 'VERBAL')}>Verbal</Button>
              <Button type="button" variant={form.modalidadFisico === 'FISICA' ? 'default' : 'outline'} className="flex-1"
                onClick={() => set('modalidadFisico', 'FISICA')}>Física</Button>
            </div>
            {form.modalidadFisico === 'VERBAL' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="transcripcion">Escrito de transcripción</Label>
                  <Textarea id="transcripcion" rows={4} value={form.transcripcionTexto}
                    onChange={(e) => set('transcripcionTexto', e.target.value)}
                    placeholder="Transcripción de la queja verbal…" />
                </div>
                <CampoAdjuntos label="PDF de la transcripción" categoria="TRANSCRIPCION" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('TRANSCRIPCION')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
              </>
            )}
          </div>
        )}

        {/* ¿Aportaron documentos? / ¿Aportaron medio magnético? */}
        {conAportes && (
          <div className="space-y-4 rounded-md border p-3">
            <div className="space-y-2">
              <Label>¿Aportaron documentos?</Label>
              <div className="flex gap-2">
                <Button type="button" variant={form.aportoDocumentos ? 'default' : 'outline'} className="flex-1"
                  onClick={() => set('aportoDocumentos', true)}>Sí</Button>
                <Button type="button" variant={!form.aportoDocumentos ? 'default' : 'outline'} className="flex-1"
                  onClick={() => set('aportoDocumentos', false)}>No</Button>
              </div>
            </div>
            {form.aportoDocumentos && (
              <>
                <CampoAdjuntos label="PDF documentos" categoria="DOCUMENTOS" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('DOCUMENTOS')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
                <CampoAdjuntos label="PDF anexos" categoria="ANEXOS" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('ANEXOS')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
              </>
            )}

            <div className="space-y-2">
              <Label>¿Aportaron medio magnético?</Label>
              <div className="flex gap-2">
                <Button type="button" variant={form.aportoMedioMagnetico ? 'default' : 'outline'} className="flex-1"
                  onClick={() => set('aportoMedioMagnetico', true)}>Sí</Button>
                <Button type="button" variant={!form.aportoMedioMagnetico ? 'default' : 'outline'} className="flex-1"
                  onClick={() => set('aportoMedioMagnetico', false)}>No</Button>
              </div>
            </div>
            {form.aportoMedioMagnetico && (
              <CampoAdjuntos label="Medio magnético" categoria="MEDIO_MAGNETICO" solicitudId={solicitud?.id}
                adjuntos={adjuntosDe('MEDIO_MAGNETICO')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
            )}
          </div>
        )}

        {/* VENTANILLA ÚNICA: ya no es un checkbox — sección fija (solo canal Correo). */}
        {esVentanilla && (
          <div className="space-y-4 rounded-md border p-3">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">Ventanilla única</p>
            <CampoAdjuntos label="PDF con el correo enviado a ventanilla única" categoria="VU_CORREO" solicitudId={solicitud?.id}
              adjuntos={adjuntosDe('VU_CORREO')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
            <CampoAdjuntos label="PDF con los documentos enviados a ventanilla única" categoria="VU_DOCUMENTOS" solicitudId={solicitud?.id}
              adjuntos={adjuntosDe('VU_DOCUMENTOS')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
            <CampoAdjuntos label="PDF de la respuesta de ventanilla única" categoria="VU_RESPUESTA" solicitudId={solicitud?.id}
              adjuntos={adjuntosDe('VU_RESPUESTA')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vurad">
                  Radicado de la ventanilla única (manual){' '}
                  {esCorreo && (
                    <span className="text-xs text-muted-foreground">(se le agrega -XXX consecutivo)</span>
                  )}
                </Label>
                <Input id="vurad" value={form.vuRadicado} onChange={(e) => set('vuRadicado', e.target.value)} />
                {/* En CORREO este radicado ES el del proceso (no hay casilla «Radicado»). */}
                {esCorreo && (
                  solicitud?.radicado_completo ? (
                    <p className="text-xs text-muted-foreground">
                      Radicado asignado:{' '}
                      <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 font-medium text-primary">
                        {solicitud.radicado_completo}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Este es el radicado del proceso: es el que aparece en el acta de reparto
                      y en el expediente.
                    </p>
                  )
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="frv">Fecha de respuesta de ventanilla única</Label>
                <Input id="frv" type="date" value={form.fechaRespuestaVentanilla} onChange={(e) => set('fechaRespuestaVentanilla', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* RUTA al finalizar el ingreso: se ELIGE AQUÍ (al final del paso 1) y lo
            escogido se diligencia en el paso siguiente. */}
        {form.canal && (
          <div className="space-y-2 rounded-md border p-3">
            <Label>Enviar a: </Label>
            {/* Elegir la ruta lleva DIRECTO al paso «Ruta y decisión» (sin Siguiente). */}
            <div className="flex gap-2">
              <Button type="button" variant={form.ruta === 'REPARTO' ? 'default' : 'outline'} className="flex-1"
                onClick={() => { set('ruta', 'REPARTO'); setPaso(1); }}>1. Reparto</Button>
              <Button type="button" variant={form.ruta === 'COMITE' ? 'default' : 'outline'} className="flex-1"
                onClick={() => { set('ruta', 'COMITE'); setPaso(1); }}>2. Comité de queja</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Al escoger pasa directo a diligenciar la ruta en el paso siguiente.
            </p>
          </div>
        )}
      </section>
      )}

      {/* Paso 2 · Se diligencia la ruta ESCOGIDA al final del Ingreso. */}
      {paso === 1 && (
      <section className="space-y-4">
        {!form.ruta && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-400/30 dark:bg-amber-950/30">
            <AlertTriangle className="size-4 shrink-0 text-amber-600" />
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Primero elija la ruta (1. Reparto o 2. Comité de queja) al final del paso de Ingreso.
            </p>
            <Button type="button" variant="outline" size="sm" onClick={() => setPaso(0)}>
              <ChevronLeft className="size-4" /> Ir al paso de Ingreso
            </Button>
          </div>
        )}

        {form.ruta && (
          <p className="text-sm text-muted-foreground">
            Ruta escogida en el Ingreso:{' '}
            <strong className="text-foreground">
              {form.ruta === 'REPARTO' ? '1. Reparto' : '2. Comité de queja'}
            </strong>{' '}
            — puede cambiarla al final del paso de Ingreso.
          </p>
        )}

        {/* Ruta REPARTO: justificación + PERSONAS DEL FLUJO (mismo diseño de
            "agregar personas" de Expedientes aprobados). Obligatorios: un abogado y un
            director de instrucción; el acta se previsualiza y se envía desde aquí. */}
        {form.ruta === 'REPARTO' && (
          <div className="space-y-4 rounded-md border p-3">
            <div className="space-y-2">
              <Label htmlFor="justrep">Justificación</Label>
              <Textarea id="justrep" rows={3} value={form.justificacionReparto}
                onChange={(e) => set('justificacionReparto', e.target.value)} />
            </div>

            {bloqueActa()}
          </div>
        )}

        {/* Ruta COMITÉ: fecha, reprogramación y decisión (solo Procede / No procede). */}
        {form.ruta === 'COMITE' && (
          <div className="space-y-4 rounded-md border p-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fcom">Fecha para el comité de queja</Label>
                <Input id="fcom" type="date" value={form.fechaComite} onChange={(e) => set('fechaComite', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>¿Se reprograma el comité?</Label>
                <div className="flex gap-2">
                  <Button type="button" variant={form.comiteReprogramado ? 'default' : 'outline'} className="flex-1"
                    onClick={() => set('comiteReprogramado', true)}>Sí</Button>
                  <Button type="button" variant={!form.comiteReprogramado ? 'default' : 'outline'} className="flex-1"
                    onClick={() => { set('comiteReprogramado', false); set('fechaComiteNueva', ''); }}>No</Button>
                </div>
              </div>
              {form.comiteReprogramado && (
                <div className="space-y-2">
                  <Label htmlFor="fcomn">Nueva fecha del comité</Label>
                  <Input id="fcomn" type="date" value={form.fechaComiteNueva} onChange={(e) => set('fechaComiteNueva', e.target.value)} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Decisión del comité</Label>
                <Select value={form.decisionComite} onValueChange={(v) => set('decisionComite', v as DecisionComite)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                  <SelectContent>
                    {DECISIONES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.decisionComite && (
                <div className="space-y-2">
                  <Label htmlFor="fdc">Fecha de la decisión del comité</Label>
                  <Input id="fdc" type="date" value={form.fechaDecisionComite} onChange={(e) => set('fechaDecisionComite', e.target.value)} />
                </div>
              )}
            </div>

            {/* Con CUALQUIER decisión se puede cargar el ACTA DEL COMITÉ
                (pedido del cliente ago-06) + el PDF de la decisión. */}
            {form.decisionComite && (
              <div className="space-y-4">
                <CampoAdjuntos label="Acta del comité" categoria="COMITE_ACTA" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('COMITE_ACTA')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
                <CampoAdjuntos label="PDF de la decisión" categoria="DECISION_COMITE" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('DECISION_COMITE')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
              </div>
            )}

            {/* REMISIÓN POR COMPETENCIA: a quién se remite. Se guarda para verlo
                al consultar el expediente. */}
            {form.decisionComite === 'REMISION_COMPETENCIA' && (
              <div className="space-y-2">
                <Label htmlFor="remdest">¿A quién se debe enviar?</Label>
                <Input id="remdest" value={form.comiteRemisionDestino} maxLength={255}
                  onChange={(e) => set('comiteRemisionDestino', e.target.value)}
                  placeholder="Entidad o autoridad a la que se remite por competencia…" />
              </div>
            )}

            {/* OTRO: «indique cuál» + constancia en PDF. */}
            {form.decisionComite === 'OTRO' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otrodet">Indique cuál</Label>
                  <Textarea id="otrodet" rows={3} value={form.comiteOtroDetalle}
                    onChange={(e) => set('comiteOtroDetalle', e.target.value)}
                    placeholder="Describa la decisión del comité…" />
                </div>
                <CampoAdjuntos label="Constancia (PDF)" categoria="COMITE_CONSTANCIA" solicitudId={solicitud?.id}
                  adjuntos={adjuntosDe('COMITE_CONSTANCIA')} onCambio={recargar} asegurarSolicitud={asegurarSolicitud} />
              </div>
            )}

            {/* Decisiones que CIERRAN: justificación + aviso de que aquí termina. */}
            {comiteCierra(form.decisionComite) && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="justnp">Justificación</Label>
                  <Textarea id="justnp" rows={3} value={form.justificacionComite}
                    onChange={(e) => set('justificacionComite', e.target.value)} />
                </div>
                <div className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive">Acá termina el proceso.</p>
                    <p className="text-destructive/80">
                      Con «{labelDecision(form.decisionComite)}» la solicitud se cierra y no
                      continúa: no pasa al Control de ingreso ni habilita la Etapa 2.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Decisiones que CONTINÚAN (Apertura / Indagación): sigue el MISMO
                bloque del acta de reparto (personas del flujo + enviar). */}
            {comiteContinua(form.decisionComite) && bloqueActa()}
          </div>
        )}
      </section>
      )}

      {/* Previsualización del acta: a nivel raíz (se abre desde el bloque del
          acta de Reparto o de Comité-PROCEDE). */}
      {actaAbierta && solicitud && (
        <ActaRepartoDialog
          // Refleja las selecciones actuales aunque aún no se hayan guardado.
          solicitud={{
            ...solicitud,
            abogado_id: form.abogadoId || solicitud.abogado_id,
            sustanciador_id: form.sustanciadorId || solicitud.sustanciador_id,
            director_id: form.directorId || solicitud.director_id,
          }}
          firmantes={firmantes}
          onClose={() => setActaAbierta(false)}
        />
      )}

      {/* Banner de estado: cada faltante es un chip clicable que salta a su paso. */}
      {solicitud && (
        solicitud.completa ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
            <CheckCircle2 className="size-4 shrink-0 text-primary" />
            <p className="font-medium text-primary">
              Pre-radicación completa: puede continuar con los datos de la notificación disciplinaria (Etapa 2).
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-400/30 dark:bg-amber-950/30">
            <AlertTriangle className="size-4 shrink-0 text-amber-600" />
            <span className="font-medium text-amber-800 dark:text-amber-300">Falta para radicar:</span>
            {solicitud.faltantes.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setPaso(PASO_POR_FALTANTE[f] ?? 0)}
                title="Ir al paso donde se corrige"
                className="rounded-full border border-amber-400/60 bg-background px-2.5 py-0.5 text-xs font-medium text-amber-800 transition-colors hover:border-amber-500 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40"
              >
                {f}
              </button>
            ))}
          </div>
        )
      )}

      {/* Guardar + flecha atrás (en el paso 2, a la izquierda de Guardar).
          Ya no hay Anterior/Siguiente: se navega con los botones de ruta,
          el stepper y esta flecha. */}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
        {!solicitud && <span className="text-sm text-muted-foreground">Sin guardar aún.</span>}
        {paso > 0 && (
          <Button
            type="button" variant="outline" size="icon"
            onClick={() => setPaso(0)} aria-label="Volver al paso de Ingreso"
            title="Volver al paso de Ingreso"
          >
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <Button type="button" onClick={() => guardar()} disabled={guardando || enviandoActa}>
          <Save className="size-4" /> {guardando ? 'Guardando…' : 'Guardar pre-radicación'}
        </Button>
      </div>

      {/* Avisos del formulario: CENTRADOS en pantalla (mismo diseño del módulo,
          en clave de error y de éxito), en vez de toasts de esquina. */}
      <AvisoErrorCentral mensaje={errorCentral} onCerrar={() => setErrorCentral(null)} />
      <AvisoExitoCentral mensaje={exitoCentral} onCerrar={() => setExitoCentral(null)} />
    </div>
  );
}
